import { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { collection, query, where, getDocs, updateDoc, doc } from "firebase/firestore";
import { db } from "../firebase";
import { message, Spin, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

export default function MyBookings() {
  const auth = getAuth();
  const user = auth.currentUser;
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [historyBookings, setHistoryBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");
  const [cancelModal, setCancelModal] = useState({ open: false, id: null, startTime: null });
  const [cancelling, setCancelling] = useState(false);

  const toggleTheme = () => {
    setDark(d => {
      localStorage.setItem("ws-theme", !d ? "dark" : "light");
      return !d;
    });
  };

  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    const fetchAll = async () => {
      if (!user) return;
      try {
        // Active reservations
        const q1 = query(collection(db, "reservation"), where("userId", "==", user.uid));
        const snap1 = await getDocs(q1);
        const active = snap1.docs.map(d => ({ id: d.id, _source: "reservation", ...d.data() }));

        // History reservations
        const q2 = query(collection(db, "reservation_history"), where("userId", "==", user.uid));
        const snap2 = await getDocs(q2);
        const history = snap2.docs.map(d => ({ id: d.id, _source: "history", ...d.data() }));

        setBookings(active.sort((a, b) => b.startTime.toDate() - a.startTime.toDate()));
        setHistoryBookings(history.sort((a, b) => b.startTime.toDate() - a.startTime.toDate()));
      } catch (err) {
        message.error("Erreur chargement des réservations.");
      }
      setLoading(false);
    };
    fetchAll();
  }, [user]);

  const handleCancel = async () => {
    const id = cancelModal.id;
    if (!id) return;
    setCancelling(true);
    try {
      const now = new Date();
      const cancelledUntil = new Date(now.getTime() + 5 * 60 * 60 * 1000); // now + 5h

      await updateDoc(doc(db, "reservation", id), {
        cancelled: true,
        cancelDate: now,
      });

      // Store the cooldown on the user's contact doc so Booking page can check it
      const contactSnap = await getDocs(
        query(collection(db, "contact"), where("FirebaseId", "==", user.uid))
      );
      if (!contactSnap.empty) {
        await updateDoc(contactSnap.docs[0].ref, { cancelledUntil });
      }

      message.success("Réservation annulée !");
      setBookings(prev => prev.map(b => b.id === id ? { ...b, cancelled: true } : b));
    } catch {
      message.error("Erreur lors de l'annulation.");
    }
    setCancelling(false);
    setCancelModal({ open: false, id: null, startTime: null });
  };

  const upcoming = bookings.filter(b => !b.cancelled && b.startTime.toDate() >= new Date());
  const past     = bookings.filter(b =>  b.cancelled || b.startTime.toDate() <  new Date());

  // Merge history items not already in active bookings
  const activeIds = new Set(bookings.map(b => b.id));
  const historyOnly = historyBookings.filter(h => !activeIds.has(h.id));

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 40, objectFit: "contain" }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: t.text1 }}>Mes Réservations</div>
              <div style={{ fontSize: 11, color: t.text2 }}>
                {bookings.length + historyOnly.length} réservation{(bookings.length + historyOnly.length) !== 1 ? "s" : ""} au total
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/booking")} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>
              ← Réservation
            </button>
            <button onClick={() => navigate("/")} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>
              🏠
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 80 }}>
            <Spin size="large" />
            <div style={{ fontSize: 13, color: t.text2, marginTop: 14 }}>Chargement...</div>
          </div>
        ) : (bookings.length === 0 && historyOnly.length === 0) ? (
          <div style={{ textAlign: "center", padding: "80px 24px" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div style={{ fontSize: 15, fontWeight: 500, color: t.text1, marginBottom: 6 }}>Aucune réservation</div>
            <div style={{ fontSize: 13, color: t.text2, marginBottom: 20 }}>Vous n'avez encore aucune réservation.</div>
            <button onClick={() => navigate("/booking")} style={{ ...s.primaryBtn, background: t.blue, padding: "10px 24px" }}>
              Faire une réservation
            </button>
          </div>
        ) : (
          <>
            {upcoming.length > 0 && (
              <>
                <SectionLabel label="À venir" count={upcoming.length} t={t} />
                <ResponsiveGrid>
                  {upcoming.map(item => (
                    <BookingCard key={item.id} item={item} t={t}
                      onCancelRequest={() => setCancelModal({ open: true, id: item.id, startTime: item.startTime?.toDate() })} />
                  ))}
                </ResponsiveGrid>
              </>
            )}

            {past.length > 0 && (
              <>
                <SectionLabel label="Réservations passées" count={past.length} t={t} />
                <ResponsiveGrid>
                  {past.map(item => (
                    <BookingCard key={item.id} item={item} t={t} />
                  ))}
                </ResponsiveGrid>
              </>
            )}

            {historyOnly.length > 0 && (
              <>
                <SectionLabel label="Historique complet" count={historyOnly.length} t={t} />
                <ResponsiveGrid>
                  {historyOnly.map(item => (
                    <BookingCard key={item.id} item={item} t={t} isHistory />
                  ))}
                </ResponsiveGrid>
              </>
            )}
          </>
        )}
      </div>

      {/* Cancel confirmation modal */}
      <Modal
        open={cancelModal.open}
        onCancel={() => setCancelModal({ open: false, id: null, startTime: null })}
        footer={null}
        centered
        width={360}
        title={<span style={{ fontSize: 15, fontWeight: 600 }}>⚠️ Annuler la réservation ?</span>}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 14, paddingTop: 4 }}>
              <p style={{ fontSize: 14, color: "#334155", margin: 0, lineHeight: 1.6 }}>
              Êtes-vous sûr de vouloir annuler cette réservation ?
            </p>

              {/* Always show: cancelling locks new bookings for 5h from NOW */}
              <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#92400e", marginBottom: 4 }}>
                  ⏱️ Attention
                </div>
                <div style={{ fontSize: 12, color: "#b45309", lineHeight: 1.5 }}>
                  Suite à cette annulation, vous ne pourrez effectuer une nouvelle réservation
                  qu'à partir de{" "}
                  <strong>{dayjs().add(5, 'hour').format('HH:mm')}</strong>
                  {" "}(dans 5 heures).
                </div>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  onClick={() => setCancelModal({ open: false, id: null, startTime: null })}
                  style={{ flex: 1, padding: "10px", borderRadius: 10, border: "1px solid #e2e8f0", background: "#f8fafc", color: "#64748b", fontSize: 13, fontWeight: 500, cursor: "pointer" }}
                >
                  Garder la réservation
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  style={{ flex: 1, padding: "10px", borderRadius: 10, border: "none", background: cancelling ? "#fecaca" : "#dc2626", color: "#fff", fontSize: 13, fontWeight: 600, cursor: cancelling ? "not-allowed" : "pointer" }}
                >
                  {cancelling ? "Annulation..." : "Oui, annuler"}
                </button>
              </div>
        </div>
      </Modal>
    </div>
  );
}

function SectionLabel({ label, count, t }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "24px 18px 10px" }}>
      <span style={{ fontSize: 11, fontWeight: 500, color: t.text3, letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</span>
      <span style={{ fontSize: 11, background: t.bg3, color: t.text2, borderRadius: 20, padding: "1px 8px" }}>{count}</span>
      <div style={{ flex: 1, height: 1, background: t.border }} />
    </div>
  );
}

function ResponsiveGrid({ children }) {
  const [cols, setCols] = useState(1);
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w >= 1024) setCols(4);
      else if (w >= 640) setCols(2);
      else setCols(1);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: 10, padding: "0 18px" }}>
      {children}
    </div>
  );
}

function BookingCard({ item, t, onCancelRequest, isHistory }) {
  const [hovered, setHovered] = useState(false);
  const start = item.startTime?.toDate();
  const end   = item.endTime?.toDate();
  const isPast = start < new Date();
  const isCancelled = item.cancelled || item.status === "cancelled";
  const tableId = item.table ?? item.deskId;

  const statusColor = isCancelled ? t.red  : isPast ? t.text3 : t.green;
  const statusBg    = isCancelled ? t.redBg : isPast ? t.bg3   : t.greenBg;
  const statusLabel = isCancelled ? "⛔ Annulée" : isPast ? "✓ Terminée" : "✅ À venir";

  return (
    <div
      style={{
        background: hovered ? t.bg3 : t.cardBg,
        border: `1px solid ${hovered ? t.blue : t.border}`,
        borderRadius: 14, padding: 16,
        transition: "border-color 0.15s, background 0.15s",
        display: "flex", flexDirection: "column", gap: 10,
        opacity: isHistory && isPast ? 0.85 : 1,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: t.blueBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>
            🪑
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: t.text1 }}>Table {tableId}</div>
            <div style={{ fontSize: 11, color: t.text2 }}>{item.fullname}</div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          <span style={{ fontSize: 11, padding: "3px 8px", borderRadius: 20, background: statusBg, color: statusColor, whiteSpace: "nowrap" }}>
            {statusLabel}
          </span>
          {isHistory && (
            <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 20, background: t.bg3, color: t.text3 }}>
              Historique
            </span>
          )}
          {item.source && (
            <span style={{ fontSize: 10, color: t.text3 }}>{item.source}</span>
          )}
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: t.border }} />

      {/* Details */}
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontSize: 12, color: t.text2 }}>📅 Date</span>
          <span style={{ fontSize: 12, fontWeight: 500, color: t.text1 }}>
            {start ? dayjs(start).format("DD/MM/YYYY") : "—"}
          </span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span style={{ fontSize: 12, color: t.text2 }}>🕒 Horaire</span>
          <span style={{ fontSize: 12, fontWeight: 500, color: t.text1 }}>
            {start && end ? `${dayjs(start).format("HH:mm")} → ${dayjs(end).format("HH:mm")}` : "—"}
          </span>
        </div>
        {item.duration && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: t.text2 }}>⏱️ Durée</span>
            <span style={{ fontSize: 12, fontWeight: 500, color: t.text1 }}>{item.duration}h</span>
          </div>
        )}
        {item.room && (
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: t.text2 }}>📍 Salle</span>
            <span style={{ fontSize: 12, fontWeight: 500, color: t.text1 }}>{item.room}</span>
          </div>
        )}
      </div>

      {/* Cancel button — only for active upcoming bookings */}
      {!isCancelled && !isPast && !isHistory && onCancelRequest && (
        <button
          onClick={onCancelRequest}
          style={{ ...s.cancelBtn, background: t.redBg, color: t.red, border: `1px solid ${t.redBorder}` }}
        >
          Annuler la réservation
        </button>
      )}
    </div>
  );
}

const theme = {
  light: {
    bg: "#f8fafc", bg3: "#f1f5f9", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueText: "#1d4ed8",
    green: "#16a34a", greenBg: "#f0fdf4",
    red: "#dc2626", redBg: "#fef2f2", redBorder: "#fecaca",
    cardBg: "#ffffff",
  },
  dark: {
    bg: "#0f172a", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueText: "#85B7EB",
    green: "#4ade80", greenBg: "#052e16",
    red: "#f87171", redBg: "#2d1515", redBorder: "#7f1d1d",
    cardBg: "#1e293b",
  },
};

const s = {
  toggleTrack: { width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", flexShrink: 0 },
  toggleKnob: { width: 14, height: 14, borderRadius: "50%", position: "absolute", top: 2, left: 2, transition: "transform 0.2s" },
  outlineBtn: { fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer" },
  primaryBtn: { fontSize: 13, fontWeight: 600, border: "none", borderRadius: 10, color: "#fff", cursor: "pointer" },
  cancelBtn: { width: "100%", padding: "8px", borderRadius: 10, fontSize: 12, fontWeight: 500, cursor: "pointer", textAlign: "center" },
};